'use client'

import { Section, Spinner, Title } from '@/components/atoms'
import { usePageTracking } from '@/hooks'
import { POPUPS_CLOSE_DELAY_MS } from '@/utils/paymentConstants'
import { parseQueryParams } from '@/utils/paymentUtils'
import { DONATION_PATH_ROOT } from '@/utils/donationPaths'
import { useRouter, useSearchParams } from 'next/navigation'
import { Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { IoWarning } from 'react-icons/io5'

/**
 * Payment Error Page
 * Handles error notification to parent (if popup) and displays error state.
 */

const COPY = {
  title: 'Ops!',
  message: 'Si è verificato un errore.',
  notifying: 'Notifica errore in corso...',
  retry: 'Riprova',
}

export default function Page() {
  return (
    <Suspense fallback={<Spinner hasText={false} />}>
      <ErroreContent />
    </Suspense>
  )
}

function ErroreContent() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const hasNotifiedRef = useRef(false)
  const [isPopup, setIsPopup] = useState(false)
  const [notifying, setNotifying] = useState(true)

  usePageTracking('donation_error')

  const queryParams = useMemo(
    () => parseQueryParams(searchParams),
    [searchParams]
  )
  const errorMessage = queryParams.error || queryParams.message || COPY.message

  useEffect(() => {
    if (typeof window === 'undefined' || hasNotifiedRef.current) return

    const popup = window.opener && !window.opener.closed
    setIsPopup(popup)

    if (popup) {
      hasNotifiedRef.current = true

      const message = {
        type: 'PAYMENT_CONFIRM_RESULT',
        success: false,
        data: {},
        error: { message: errorMessage, ...queryParams },
        paymentType: queryParams.paymentType || 'unknown',
      }

      try {
        window.opener.postMessage(message, window.location.origin)
        setTimeout(() => window.close(), POPUPS_CLOSE_DELAY_MS)
      } catch (err) {
        console.error('[ErrorPage] Error notifying parent:', err)
        setNotifying(false)
      }
    } else {
      setNotifying(false)
    }
  }, [errorMessage, queryParams])

  if (notifying) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <Spinner hasText={false} />
        <p style={{ marginTop: '1rem', color: '#666' }}>{COPY.notifying}</p>
      </div>
    )
  }

  return (
    <Section className='error-section'>
      <div className='error-section__head'>
        <IoWarning className='error-section__head__icon' />
        <Title
          level={1}
          className='error-section__head__title'
        >
          {COPY.title}
        </Title>
        <p>{errorMessage}</p>
      </div>

      {!isPopup && (
        <div className='error-section__cta-wrap'>
          <button
            onClick={() => router.push(DONATION_PATH_ROOT)}
            className='error-section__cta-wrap__button error-section__cta-wrap__button--primary'
          >
            {COPY.retry}
          </button>
        </div>
      )}
    </Section>
  )
}
